// src/ui/screens/parent.ts
// THE PARENT SURFACE. Read-only, own children only, and the strictest screen in this app.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// TWO RULES, BOTH FROM THE BRIEF, BOTH ENFORCED HERE BY WHAT IS *ABSENT*
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// 1. `NO MEASURABLE CHANGE YET` IS RENDERED EXACTLY AS THE SERVER SENDS IT.
//    The backend has this as a constant (baselines.ts NO_MEASURABLE_CHANGE_YET) reached through a
//    noise-floor and smallest-worthwhile-change gate, and it means something precise: the
//    measurement cannot support a claim of improvement. This screen prints `item.sentence` verbatim.
//    It does not shorten it, does not soften it to "keep going!", does not swap in an encouraging
//    variant, and does not hide the item because the sentence is unexciting. Rewriting it here would
//    undo the entire gate — a parent would read a claim the measurement does not support.
//
// 2. THERE IS NO BIOMECHANICS WALL, AND NOTHING HERE CAN BECOME ONE.
//    The server already withholds the raw layer: `ParentProgressItem` carries `what` (a human
//    label), `state`, one `sentence`, and `change_shown` which is NON-NULL ONLY under
//    MEANINGFUL_CHANGE. There is no angle, no velocity, no per-delivery row, no chart. This screen
//    renders those four fields and nothing else — in particular it never falls back to dumping the
//    raw JSON of an item it does not recognise, which is the accident that would put a wall of
//    joint angles in front of a parent. An unrecognised state renders its label and its sentence,
//    which are always safe, and stops.
//
// And `what_this_page_does_not_show` — the backend's own list of withheld things — is printed,
// because a page that quietly omits is different from a page that says what it omits.

import { el, empty, field, mount, section, spinner } from '../dom';
import { refusalView } from '../refusal-view';
import { isRefusal } from '../../api/client';
import {
  parentChildren, parentClipPlayback, parentClips, parentReport,
  type ApprovedClip, type ParentProgressItem,
} from '../../api/practice';
import { getState, selectAthlete } from '../state';

export async function parentScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  mount(root, spinner('Loading your children…'));
  const kids = await parentChildren();
  const out = el('div');

  if (isRefusal(kids)) {
    mount(root, section('Your children', refusalView(kids, { onRetry: rerender })));
    return;
  }

  const children = kids.value.children ?? [];
  out.append(section('Your children',
    // The note explains that an unverified link grants nothing. It is the server's sentence.
    el('p', { class: 'note' }, kids.value.note),
    children.length
      ? el('div', { class: 'list' }, ...children.map((c) => el('button', {
          class: `row-item row-tap${getState().athlete?.athleteId === c.athlete_id ? ' row-selected' : ''}`,
          onclick: () => {
            selectAthlete({
              athleteId: c.athlete_id,
              from: 'parent_children', // ← provenance: the server enumerated and gated this id
              academyId: null,
              label: c.athlete_id,
              subjectIsMinor: c.subject_is_minor,
            });
            rerender();
          },
        },
          el('p', { class: 'mono small' }, c.athlete_id),
          field('Your role', c.viewer_role),
          c.subject_is_minor && el('span', { class: 'tag tag-minor' }, 'minor'),
        )))
      : empty('No verified child links on this account. An unverified link is kept as a record but '
        + 'grants nothing until it is verified.'),
  ));

  const sel = getState().athlete;
  if (sel && sel.from === 'parent_children') {
    out.append(await reportPanel(sel.athleteId, rerender));
    out.append(await clipsPanel(sel.athleteId));
  } else {
    out.append(section('Report', empty('Choose a child above to see their report.')));
  }
  mount(root, out);
}

async function reportPanel(athleteId: string, rerender: () => void): Promise<HTMLElement> {
  const r = await parentReport(athleteId);
  if (isRefusal(r)) return section('Report', refusalView(r, { onRetry: rerender }));
  const rep = r.value;

  return section('Report',
    field('Window', `${rep.window.days} days, ${rep.window.from.slice(0, 10)} → ${rep.window.to.slice(0, 10)}`),

    el('h3', {}, 'Participation'),
    field('Sessions attended', rep.participation.sessions_attended),
    field('Balls recorded', rep.participation.balls_recorded),
    field('Days practised', rep.participation.days_practised),
    field('Last session', rep.participation.last_session_at ?? 'none in this window'),

    el('h3', {}, 'Current focus'),
    rep.current_focus.focus.length
      ? el('ul', {}, ...rep.current_focus.focus.map((f) => el('li', {}, f)))
      : empty('No focus set for this window.'),
    el('p', { class: 'note' }, rep.current_focus.note),

    el('h3', {}, 'Progress'),
    // The standing scope sentence, verbatim: phone-camera estimates, not laboratory measurements.
    el('p', { class: 'note' }, rep.progress.scope),
    rep.progress.items.length
      ? el('div', { class: 'list' }, ...rep.progress.items.map(progressItem))
      : empty('No progress items for this window.'),
    el('p', { class: 'note' }, rep.progress.note),

    el('h3', {}, 'Workload and rest'),
    field('State', rep.workload_and_rest.state),
    // The guardian module's own closed set of sentences, unedited.
    ...rep.workload_and_rest.guidance.map((g) => el('p', { class: 'note' }, g)),
    el('p', { class: 'note' }, rep.workload_and_rest.scope),

    el('h3', {}, 'Coach comments'),
    rep.coach_comments.length
      ? el('div', { class: 'list' }, ...rep.coach_comments.map((c) => el('div', { class: 'row-item' },
          el('p', {}, c.comment),
          el('p', { class: 'small' }, new Date(c.at).toLocaleString()),
          c.drill && el('div', { class: 'kv' },
            field('Drill', c.drill.title),
            // The safety note ships WITH the drill. Showing the drill without it would be worse
            // than showing neither.
            el('p', { class: 'warn' }, `When not to do it: ${c.drill.when_not_to_do_it}`)),
        )))
      : empty('No coach comments in this window.'),

    el('h3', {}, 'What this page does not show'),
    // The backend's own list of withheld things. Printed, not summarised.
    el('ul', {}, ...rep.what_this_page_does_not_show.map((s) => el('li', {}, s))),
  );
}

/**
 * ONE progress item.
 *
 * `sentence` is printed EXACTLY as received — this is where `NO MEASURABLE CHANGE YET` reaches the
 * screen. `change_shown` is printed only when the server sent one, which it does only under
 * MEANINGFUL_CHANGE; there is no branch here that composes a number of its own from anything.
 */
function progressItem(item: ParentProgressItem): HTMLElement {
  return el('div', { class: `row-item progress-${item.state.toLowerCase()}` },
    el('p', { class: 'progress-what' }, item.what),
    el('span', { class: 'tag' }, item.state),
    // ── VERBATIM. Not shortened, not softened, not substituted.
    el('p', { class: 'progress-sentence' }, item.sentence),
    item.change_shown && el('p', { class: 'progress-change' }, item.change_shown),
    el('p', { class: 'small' },
      item.reviewed_by_a_coach
        ? 'A coach has looked at the underlying measurements.'
        : 'No coach has reviewed the underlying measurements yet.'),
  );
}

async function clipsPanel(athleteId: string): Promise<HTMLElement> {
  const r = await parentClips(athleteId);
  if (isRefusal(r)) return section('Approved clips', refusalView(r));
  const raw = r.value;
  const clips: ApprovedClip[] = Array.isArray(raw)
    ? raw
    : ((raw as { clips?: ApprovedClip[] }).clips ?? []);

  return section('Approved clips',
    el('p', { class: 'note' },
      'A clip appears here only after a coach explicitly approved it, and a family can withdraw '
      + 'that approval at any time. The video address is not fetched with this list — it is '
      + 'resolved one clip at a time, when you tap play, so a withdrawal takes effect immediately '
      + 'and there is no durable link left behind.'),
    clips.length
      ? el('div', { class: 'list' }, ...clips.map(clipRow.bind(null, athleteId)))
      : empty('No clips have been approved for you to watch.'),
  );
}

function clipRow(athleteId: string, c: ApprovedClip): HTMLElement {
  const target = el('div');
  return el('div', { class: 'row-item' },
    field('Camera', c.camera_role),
    field('Recorded', c.recorded_at ?? 'unknown'),
    field('Approved', c.approved_at ?? 'unknown'),
    c.note && el('p', { class: 'note' }, c.note),
    el('button', {
      class: 'btn',
      // Resolved ON TAP only. Prefetching would turn the server's live re-decision into a stale one.
      onclick: () => {
        void (async () => {
          mount(target, spinner('Checking whether this clip may still be played…'));
          const p = await parentClipPlayback(athleteId, c.clip_id);
          if (isRefusal(p)) { mount(target, refusalView(p)); return; }
          const url = typeof p.value['url'] === 'string' ? (p.value['url'] as string) : null;
          mount(target, url
            ? el('video', { class: 'clip', src: url, controls: true, playsinline: true })
            : el('p', { class: 'note' },
                'The server allowed this clip but returned no playable address — most likely because '
                + 'video storage is not enabled on this deployment yet.'));
        })();
      },
    }, 'Play'),
    target,
  );
}
